import { useCallback, useEffect, useMemo, useState } from 'react'
import * as api from '../lib/api'
import Modal from '../components/Modal'
import Select from '../components/Select'
import { SkeletonRow } from '../components/Skeleton'
import { CATEGORY_LABEL, categoryLabel } from '../lib/remediation'
import { whenText } from '../lib/when'

/**
 * Accounts Team — admins manage who's on the accounts team: add someone, edit
 * which fixes they get and who covers them, deactivate someone who's left.
 * Being on this list (and active) is what lets a person sign in to Lereco and
 * see My Ledgers.
 */

const CATEGORIES = Object.keys(CATEGORY_LABEL)
const ROW_COLS = 'minmax(190px,1.6fr) 120px 130px minmax(140px,1fr) 90px 90px 110px 170px'

const statusOf = (a) => (a.active === false ? 'inactive' : a.awaySince ? 'away' : 'active')
const STATUS = {
  active: { label: 'Active', cls: 'p-ok' },
  away: { label: 'Away', cls: 'p-warn' },
  inactive: { label: 'Deactivated', cls: 'p-mute' },
}

export default function AccountsTeam() {
  const [team, setTeam] = useState(null)
  const [openFixes, setOpenFixes] = useState({})
  const [error, setError] = useState(null)
  const [editing, setEditing] = useState(null) // { person } or { person: null } for add
  const [leaving, setLeaving] = useState(null)
  const [toast, setToast] = useState(null)

  const load = useCallback(async () => {
    try {
      const [dir, fixes] = await Promise.all([api.listTeam(), api.listRemediationItems({ status: 'all' })])
      setTeam(dir.accountants ?? [])
      const counts = {}
      for (const i of fixes.items ?? []) {
        if (i.status !== 'confirmed' && i.assignedAccountantEmail) counts[i.assignedAccountantEmail] = (counts[i.assignedAccountantEmail] ?? 0) + 1
      }
      setOpenFixes(counts)
      setError(null)
    } catch (e) {
      setError(e.message)
      setTeam([])
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    if (!toast) return undefined
    const t = setTimeout(() => setToast(null), 3500)
    return () => clearTimeout(t)
  }, [toast])

  const active = useMemo(() => (team ?? []).filter((a) => a.active !== false), [team])
  const owners = useMemo(() => Object.fromEntries(CATEGORIES.map((c) => [c, active.find((a) => a.routesCategory === c) ?? null])), [active])
  const nameOf = (email) => (team ?? []).find((a) => a.email === email)?.name ?? email
  const sorted = useMemo(
    () => [...(team ?? [])].sort((a, b) => (a.active === false) - (b.active === false) || a.name.localeCompare(b.name)),
    [team]
  )

  const done = (message) => {
    setEditing(null)
    setLeaving(null)
    setToast(message)
    load()
  }

  return (
    <section className="screen">
      <div className="topbar">
        <div>
          <h1 className="page-title">Accounts Team</h1>
          <div className="page-sub">
            Who's on the accounts team, which fixes each person gets, and who covers them when they're away. Being on this
            list is what lets someone sign in to Lereco and see My Ledgers.
          </div>
        </div>
        <button type="button" className="btn btn-primary" onClick={() => setEditing({ person: null })}>
          + Add accountant
        </button>
      </div>

      {error && (
        <div className="note" style={{ borderColor: 'var(--alert)' }}>
          {error}
        </div>
      )}

      {/* Who each fix category goes to — the routing, at a glance. */}
      <div className="stats tm-routes">
        {CATEGORIES.map((c) => (
          <div key={c} className="stat">
            <div className="stat-label">{categoryLabel(c)} fixes go to</div>
            <div className={`tm-owner${owners[c] ? '' : ' none'}`}>{owners[c]?.name ?? 'Nobody'}</div>
            <div className="stat-meta">
              {owners[c]
                ? owners[c].awaySince
                  ? 'Away — going to their backup'
                  : `${openFixes[owners[c].email] ?? 0} open on them`
                : c === 'OTHER'
                  ? 'New fixes won’t be assigned to anyone'
                  : `Going to the Other owner${owners.OTHER ? ` (${owners.OTHER.name})` : ''}`}
            </div>
          </div>
        ))}
      </div>

      <div className="tbl">
        <div className="thead" style={{ gridTemplateColumns: ROW_COLS }}>
          <span>Accountant</span>
          <span>Binny login</span>
          <span>Gets fixes for</span>
          <span>Backup</span>
          <span>Cliq</span>
          <span>Keka</span>
          <span>Status</span>
          <span />
        </div>
        {team === null && <SkeletonRow cols={ROW_COLS} cells={8} count={5} />}
        {team !== null && team.length === 0 && <div className="empty">Nobody on the team yet — add the first accountant.</div>}
        {sorted.map((a) => {
          const st = statusOf(a)
          return (
            <div
              key={a.email}
              className={`rowgrid${st === 'inactive' ? ' tm-off' : ''}`}
              style={{ gridTemplateColumns: ROW_COLS }}
              role="button"
              tabIndex={0}
              title="Edit"
              onClick={() => setEditing({ person: a })}
              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), setEditing({ person: a }))}
            >
              <span>
                <span className="c-name">{a.name}</span>
                <span className="c-sub">
                  {a.email}
                  {a.getsEscalations && <span className="tm-alerts" title="Gets “needs a person” alerts on Cliq">alerts</span>}
                </span>
              </span>
              <span className="c-sub" style={{ marginTop: 0 }}>{a.visionUsername || <span className="rm-warn">not set</span>}</span>
              <span>{a.routesCategory ? <span className="rm-cat">{categoryLabel(a.routesCategory)}</span> : <span className="mute">—</span>}</span>
              <span className="c-sub" style={{ marginTop: 0 }}>
                {(a.backupEmails ?? []).length ? a.backupEmails.map(nameOf).join(', ') : <span className="mute">none</span>}
              </span>
              <span className="c-sub" style={{ marginTop: 0 }}>{a.zuid ? 'Linked' : <span className="rm-warn">No ID</span>}</span>
              <span className="c-sub" style={{ marginTop: 0 }}>{a.kekaEmployeeId ? 'Linked' : <span className="mute">—</span>}</span>
              <span>
                <span className={`pill ${STATUS[st].cls}`}>
                  <span className="d" />
                  {STATUS[st].label}
                </span>
                {st === 'away' && <span className="c-sub">since {whenText(a.awaySince)}</span>}
              </span>
              <span className="tm-acts" onClick={(e) => e.stopPropagation()}>
                <button type="button" className="btn btn-sm" onClick={() => setEditing({ person: a })}>
                  Edit
                </button>
                {st === 'inactive' ? (
                  <button
                    type="button"
                    className="btn btn-sm"
                    onClick={async () => {
                      try {
                        await api.reactivateAccountant(a.email)
                        done(`${a.name} is back on the team.`)
                      } catch (e) {
                        setToast(e.message)
                      }
                    }}
                  >
                    Reactivate
                  </button>
                ) : (
                  <button type="button" className="btn btn-sm btn-danger" onClick={() => setLeaving(a)}>
                    Deactivate
                  </button>
                )}
              </span>
            </div>
          )
        })}
      </div>

      {editing && (
        <PersonModal
          person={editing.person}
          team={active}
          owners={owners}
          onClose={() => setEditing(null)}
          onDone={(name, added) => done(added ? `${name} added to the team.` : `${name} updated.`)}
        />
      )}
      {leaving && (
        <DeactivateModal
          person={leaving}
          open={openFixes[leaving.email] ?? 0}
          team={active.filter((a) => a.email !== leaving.email)}
          onClose={() => setLeaving(null)}
          onDone={(out) =>
            done(
              `${leaving.name} deactivated${out.moved ? ` — ${out.moved} fix${out.moved === 1 ? '' : 'es'} moved to ${nameOf(out.movedTo)}` : ''}.`
            )
          }
        />
      )}
      {toast && (
        <div className="toast-wrap">
          <div className="toast toast-ok">{toast}</div>
        </div>
      )}
    </section>
  )
}

/** Add or edit one accountant. */
function PersonModal({ person, team, owners, onClose, onDone }) {
  const adding = !person
  const [form, setForm] = useState(() => ({
    name: person?.name ?? '',
    email: person?.email ?? '',
    visionUsername: person?.visionUsername ?? '',
    routesCategory: person?.routesCategory ?? '',
    backups: [...(person?.backupEmails ?? []), '', '', ''].slice(0, 3),
    zuid: person?.zuid ?? '',
    kekaEmployeeId: person?.kekaEmployeeId ?? '',
    getsEscalations: Boolean(person?.getsEscalations),
  }))
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }))
  const text = (k) => (e) => set(k)(e.target.value)

  const others = team.filter((a) => a.email !== person?.email)
  const taken = form.routesCategory && owners[form.routesCategory] && owners[form.routesCategory].email !== person?.email ? owners[form.routesCategory] : null

  const submit = async () => {
    setBusy(true)
    setError(null)
    const body = {
      name: form.name,
      visionUsername: form.visionUsername,
      routesCategory: form.routesCategory || null,
      backupEmails: form.backups.filter(Boolean),
      zuid: form.zuid,
      kekaEmployeeId: form.kekaEmployeeId,
      getsEscalations: form.getsEscalations,
    }
    try {
      if (adding) await api.addAccountant({ ...body, email: form.email })
      else await api.updateAccountant(person.email, body)
      onDone(form.name.trim() || form.email, adding)
    } catch (e) {
      setError(e.message)
      setBusy(false)
    }
  }

  return (
    <Modal
      title={adding ? 'Add an accountant' : `Edit ${person.name}`}
      onClose={busy ? () => {} : onClose}
      wide
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={submit} disabled={busy}>
            {busy ? 'Saving…' : adding ? 'Add to team' : 'Save'}
          </button>
        </>
      }
    >
      <div className="tm-form">
        <div className="frow">
          <label className="field">
            <span className="rm-lbl">Name</span>
            <input type="text" value={form.name} onChange={text('name')} placeholder="e.g. Neha Shah" autoFocus />
          </label>
          <label className="field">
            <span className="rm-lbl">Work email</span>
            <input type="email" value={form.email} onChange={text('email')} placeholder="neha.shah@binnys.in" disabled={!adding} />
            {!adding && <span className="tm-hint">Can’t be changed — it’s how their fixes are tracked.</span>}
          </label>
        </div>
        <div className="frow">
          <label className="field">
            <span className="rm-lbl">Binny username</span>
            <input type="text" value={form.visionUsername} onChange={text('visionUsername')} placeholder="their Binny login" />
            <span className="tm-hint">What they sign in with. Lets them into My Ledgers.</span>
          </label>
          <div className="field">
            <span className="rm-lbl">Gets fixes for</span>
            <Select
              options={[{ value: '', label: 'Nothing (no fixes routed)' }, ...CATEGORIES.map((c) => ({ value: c, label: categoryLabel(c) }))]}
              value={form.routesCategory}
              onChange={set('routesCategory')}
              title="Which fix category lands on them"
            />
            {taken && <span className="tm-hint warn">Currently {taken.name}’s — saving moves it to this person.</span>}
          </div>
        </div>
        <div className="field">
          <span className="rm-lbl">Backups, in order</span>
          <div className="tm-backups">
            {[0, 1, 2].map((n) => (
              <Select
                key={n}
                options={[
                  { value: '', label: n === 0 ? 'No backup' : '—' },
                  ...others
                    .filter((a) => !form.backups.some((b, i) => b === a.email && i !== n))
                    .map((a) => ({ value: a.email, label: a.name })),
                ]}
                value={form.backups[n]}
                onChange={(v) => setForm((f) => ({ ...f, backups: f.backups.map((b, i) => (i === n ? v : b)) }))}
                title={`Backup ${n + 1}`}
              />
            ))}
          </div>
          <span className="tm-hint">When they’re away, their not-started fixes go to the first of these who’s in.</span>
        </div>
        <div className="frow">
          <label className="field">
            <span className="rm-lbl">Cliq ID</span>
            <input type="text" value={form.zuid} onChange={text('zuid')} placeholder="e.g. 60035175429" />
            <span className="tm-hint">Needed for their daily Cliq reminder.</span>
          </label>
          <label className="field">
            <span className="rm-lbl">Keka employee ID (optional)</span>
            <input type="text" value={form.kekaEmployeeId} onChange={text('kekaEmployeeId')} placeholder="found from their email if left blank" />
            <span className="tm-hint">For marking them away automatically from Keka attendance.</span>
          </label>
        </div>
        <label className="tm-check">
          <input type="checkbox" checked={form.getsEscalations} onChange={(e) => set('getsEscalations')(e.target.checked)} />
          <span>
            <b>Gets “needs a person” alerts</b>
            <span className="tm-hint">
              A Cliq message the moment a client needs a person — 7 asks after reconciliation with no corrected ledger, or a
              refusal. Needs a Cliq ID.
            </span>
          </span>
        </label>
        {error && <div className="rm-error">{error}</div>}
      </div>
    </Modal>
  )
}

/** Take someone off the team — their open fixes go to whoever the admin picks. */
function DeactivateModal({ person, open, team, onClose, onDone }) {
  const [moveTo, setMoveTo] = useState(person.backupEmails?.find((b) => team.some((a) => a.email === b)) ?? '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  const submit = async () => {
    setBusy(true)
    setError(null)
    try {
      onDone(await api.deactivateAccountant(person.email, { moveTo: open ? moveTo : null }))
    } catch (e) {
      setError(e.message)
      setBusy(false)
    }
  }

  return (
    <Modal
      title={`Deactivate ${person.name}?`}
      onClose={busy ? () => {} : onClose}
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button className="btn btn-danger" onClick={submit} disabled={busy || (open > 0 && !moveTo)}>
            {busy ? 'Saving…' : 'Deactivate'}
          </button>
        </>
      }
    >
      <div className="rm-confirm">
        <p className="rm-confirm-lead">
          They won’t be able to sign in to Lereco any more and won’t get Cliq reminders. Their history stays, and you can
          reactivate them later.
          {person.routesCategory && (
            <>
              {' '}
              <b>{categoryLabel(person.routesCategory)}</b> fixes will have no owner until you give them to someone.
            </>
          )}
        </p>
        {open > 0 ? (
          <div className="field" style={{ marginBottom: 0 }}>
            <span className="rm-lbl">
              Move their {open} open fix{open === 1 ? '' : 'es'} to
            </span>
            <Select
              options={[{ value: '', label: 'Pick someone' }, ...team.map((a) => ({ value: a.email, label: a.name }))]}
              value={moveTo}
              onChange={setMoveTo}
              title="Who takes over their open fixes"
            />
            <span className="tm-hint">Anything they’re working on is paused first, with the time kept.</span>
          </div>
        ) : (
          <p className="tm-hint">They have no open fixes.</p>
        )}
        {error && <div className="rm-error">{error}</div>}
      </div>
    </Modal>
  )
}
